import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { CustomTableReportEquipmentLogbook } from 'src/app/interfaces/custom-table-report-equipment-logbook/custom-table-report-equipment-logbook.interface';
import { MaintenanceReportFilterModel } from 'src/app/models/maintenance/maintenance-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceReportService } from 'src/app/services/maintenance/maintenance-report.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-equipment-logbook-report',
  templateUrl: './equipment-logbook-report.component.html',
  styleUrls: ['./equipment-logbook-report.component.scss'],
  standalone: false
})
export class EquipmentLogbookReportComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly articleService = inject(ArticleService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly maintenanceReportService = inject(MaintenanceReportService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReportEquipmentLogbook;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formMaintenanceReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listReportType: CustomListDropdown[] = [
    { value: 0, label: 'H.V. Simple' },
    { value: 1, label: 'H.V. Valorizada' }
  ];
  nameWindow: string = 'Hoja de vida';
  projectName = ProjectName.projectName;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeArticle(articleId: number | null) {
    if (articleId == 0 || articleId == null) {
      this.formMaintenanceReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
  }

  async exportExcel() {
    if (this.changeFilters) {
      try {
        const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
        const reportType = this.formMaintenanceReportFilter.get('reportType')?.value;
        const response = reportType === 0 ?
          await firstValueFrom(this.maintenanceReportService.exportSimpleEquipmentLogbook(model)) :
          await firstValueFrom(this.maintenanceReportService.exportValuedEquipmentLogbook(model));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = reportType === 0 ? `Hoja de vida simple.xlsx` : `Hoja de vida valorizada.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para exportar.');
    }
  }

  formatOrderTotals(nodes: any[], reportType: number) {
    for (const node of nodes) {
      // Identifico el nivel de orden de mantenimiento por la cadena de texto
      if (node.type === 'group' && node.name && node.name.toString().startsWith('Orden Mmto:')) {
        const totalCost = node.repairTotalCost;
        node.colspan = 6;
        //Elimino la propiedad para que no aparezca en la tabla y no se confunda con los totales anteriores
        delete node.repairTotalCost;
        // Agrego el nodo de total al final de los hijos de esta orden
        if (node.children && reportType === 1) {
          const totalNode: any = { type: 'detail' };
          totalNode['repairUnitValue'] = 'Total Orden Mmto:';
          totalNode['repairTotalCost'] = totalCost;
          node.children.push(totalNode);
        }
      }
      // Llamada recursiva para aplicarla a todas las ordenes de mantenimiento
      if (node.children && node.children.length > 0) {
        this.formatOrderTotals(node.children, reportType);
      }
    }
  }

  async getAllLists() {
    try {
      const articles = await firstValueFrom(this.articleService.getArticlesEquipmentLogbookForSelectAsync());
      this.listArticle = articles;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para imprimir.');
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formMaintenanceReportFilter.valid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
    if (model.finalDate < model.initialDate) {
      this.toastHandlerService.showToast('warning', 'La fecha final no puede ser menor a la fecha inicial.');
      return;
    }
    try {
      const reportType = this.formMaintenanceReportFilter.get('reportType')?.value;
      if (reportType === 0) {
        this.dataReport = await firstValueFrom(this.maintenanceReportService.getSimpleEquipmentLogbook(model));
      } else {
        this.dataReport = await firstValueFrom(this.maintenanceReportService.getValuedEquipmentLogbook(model));
      }
      if (this.dataReport && (this.dataReport.data?.length > 0 || this.dataReport.equipmentLogbookInfo?.length > 0)) {
        if (this.dataReport.data?.length > 0) {
          this.formatOrderTotals(this.dataReport.data, reportType);
        }
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast('info', 'No se encontraron datos.');
      }
    } catch (error) {
      this.changeFilters = false;
      console.error("Error al consultar el reporte:", error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      reportType: {
        required: 'El Tipo de Informe es obligatorio.'
      },
      initialDate: {
        required: 'La fecha inicial es obligatoria.'
      },
      finalDate: {
        required: 'La fecha final es obligatoria.'
      },
      articleId: {
        required: 'El Artículo es obligatorio.',
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formMaintenanceReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formMaintenanceReportFilter = this.formBuilder.group({
      reportType: [0, [Validators.required]],
      initialDate: [moment('2000-01-01').toDate(), [Validators.required]],
      finalDate: [moment('3000-12-31').toDate(), [Validators.required]],
      articleId: [null, [Validators.required, CustomValidators.numberOnlyValidator()]]
    });
  }

  validateFormControls() {
    Object.keys(this.formMaintenanceReportFilter.controls).forEach((key) => {
      const control = this.formMaintenanceReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
  }

}
